import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  AUTOMATIC_NEXT_STATES_BY_DISPLAY_STATE,
  OPERATIONS_NEXT_STATES_BY_DISPLAY_STATE,
  SALES_NEXT_STATES_BY_DISPLAY_STATE
} from '../../programming-state-transition';

interface FileStateInfo {
  state: string;
  description: string;
  userHint: string;
  phase: FileStatePhase;
  phaseTitle: string;
  visibleInOperations: boolean;
  visibleInSales: boolean;
  operationsNextStates: string[];
  salesNextStates: string[];
  automaticNextStates: string[];
  order: number;
}

interface FileStateGroup {
  phase: FileStatePhase;
  title: string;
  description: string;
  states: FileStateInfo[];
}

type FileStatePhase = 'sales' | 'programming' | 'operation' | 'closing';

interface FileStateDetail {
  description: string;
  userHint: string;
  phase: FileStatePhase;
  order: number;
}

const PHASES: Array<Omit<FileStateGroup, 'states'>> = [
  {
    phase: 'sales',
    title: 'Preparación comercial',
    description: 'Estados que nacen en Sales antes o alrededor de la creación del file.'
  },
  {
    phase: 'programming',
    title: 'Programación operativa',
    description: 'Estados donde Operations revisa, confirma y programa la operación.'
  },
  {
    phase: 'operation',
    title: 'Ejecución del file',
    description: 'Estados ligados al inicio y fin real de la operación.'
  },
  {
    phase: 'closing',
    title: 'Cierre y excepciones',
    description: 'Estados finales o de excepción del flujo.'
  }
];

const STATE_DETAILS: Record<string, FileStateDetail> = {
  'BORRADOR': {
    description: 'Itinerario creado en Sales, aún sin aprobación comercial.',
    userHint: 'Operations no debe intervenir todavía; el file todavía no está listo para programación.',
    phase: 'sales',
    order: 10
  },
  'COTIZADO': {
    description: 'Sales ya cotizó el itinerario, pero todavía no confirma el file.',
    userHint: 'Sirve como referencia comercial; no aparece como trabajo operativo de Programación.',
    phase: 'sales',
    order: 20
  },
  'NO APROBADO': {
    description: 'Sales marcó que el itinerario no fue aprobado.',
    userHint: 'No tiene siguiente paso operativo desde Operations.',
    phase: 'sales',
    order: 30
  },
  'APROBADO': {
    description: 'Sales aprobó el itinerario y puede generar el número de file.',
    userHint: 'Sales puede anularlo; es la antesala comercial para que llegue un pedido a Operations.',
    phase: 'sales',
    order: 40
  },
  'NUEVO PEDIDO': {
    description: 'Pedido recibido por Operations para iniciar revisión y preparación.',
    userHint: 'Operations puede pasarlo a EN PROCESO; Sales puede anularlo o postergarlo.',
    phase: 'programming',
    order: 50
  },
  'LAST MINUTE': {
    description: 'File urgente que requiere atención inmediata de Programación.',
    userHint: 'Operations puede programarlo; Sales puede anularlo.',
    phase: 'programming',
    order: 60
  },
  'REVISAR': {
    description: 'Hay información pendiente o incidentes que deben revisarse.',
    userHint: 'Primero se deben resolver las observaciones; solo Sales puede anularlo.',
    phase: 'programming',
    order: 70
  },
  'EN PROCESO': {
    description: 'Operations está validando servicios, pasajeros, vuelos y preparación operativa.',
    userHint: 'Operations puede confirmarlo; Sales puede anularlo.',
    phase: 'programming',
    order: 80
  },
  'CONFIRMADO': {
    description: 'Operations terminó la validación principal del file.',
    userHint: 'Sales puede pasarlo a RECONFIRMADO o anularlo.',
    phase: 'programming',
    order: 90
  },
  'RECONFIRMADO': {
    description: 'Sales reconfirmó el file y lo devuelve listo para programación operativa.',
    userHint: 'Operations puede pasarlo a PROGRAMADO; Sales puede anularlo.',
    phase: 'programming',
    order: 100
  },
  'PROGRAMADO': {
    description: 'El file quedó programado para ejecución.',
    userHint: 'Solo Sales puede anularlo; el sistema lo pasa a EN OPERACION el día de operación.',
    phase: 'operation',
    order: 110
  },
  'EN OPERACION': {
    description: 'El file está en ejecución durante sus fechas operativas.',
    userHint: 'Este estado lo administra el sistema; no se puede anular. Al finalizar pasará a OPERADO.',
    phase: 'operation',
    order: 120
  },
  'OPERADO': {
    description: 'La operación terminó y el file queda listo para cierre.',
    userHint: 'Operations o Sales pueden cerrarlo; no se puede anular desde este estado.',
    phase: 'closing',
    order: 130
  },
  'CERRADO': {
    description: 'El file ya fue cerrado.',
    userHint: 'No se puede anular; Sales puede reabrirlo como NUEVO PEDIDO si corresponde.',
    phase: 'closing',
    order: 140
  },
  'ANULADO': {
    description: 'El file o pedido fue anulado por Sales.',
    userHint: 'Estado final; no tiene siguientes pasos desde Programación.',
    phase: 'closing',
    order: 150
  },
  'POSTERGADO': {
    description: 'Sales postergó el file o pedido.',
    userHint: 'Sales puede devolverlo a NUEVO PEDIDO o anularlo.',
    phase: 'closing',
    order: 160
  }
};

const uniqueStates = (...stateGroups: Array<Record<string, unknown>>): string[] =>
  Array.from(new Set(stateGroups.flatMap((group) => Object.keys(group))));

@Component({
  selector: 'app-file-states-info',
  templateUrl: './file-states-info.component.html',
  styleUrls: ['./file-states-info.component.css']
})
export class FileStatesInfoComponent {
  readonly stateRows: FileStateInfo[] = uniqueStates(
    SALES_NEXT_STATES_BY_DISPLAY_STATE,
    OPERATIONS_NEXT_STATES_BY_DISPLAY_STATE,
    AUTOMATIC_NEXT_STATES_BY_DISPLAY_STATE
  )
    .map((state) => {
      const detail = STATE_DETAILS[state] ?? {
        description: 'Estado del flujo de file.',
        userHint: 'Revisa las acciones disponibles para saber qué área interviene.',
        phase: 'programming' as FileStatePhase,
        order: 999
      };

      return {
        state,
        description: detail.description,
        userHint: detail.userHint,
        phase: detail.phase,
        phaseTitle: PHASES.find((phase) => phase.phase === detail.phase)?.title ?? '',
        visibleInOperations: state in OPERATIONS_NEXT_STATES_BY_DISPLAY_STATE,
        visibleInSales: state in SALES_NEXT_STATES_BY_DISPLAY_STATE,
        operationsNextStates: OPERATIONS_NEXT_STATES_BY_DISPLAY_STATE[state] ?? [],
        salesNextStates: SALES_NEXT_STATES_BY_DISPLAY_STATE[state] ?? [],
        automaticNextStates: (AUTOMATIC_NEXT_STATES_BY_DISPLAY_STATE[state] ?? []).map(
          (transition) => transition.toState
        ),
        order: detail.order
      };
    })
    .sort((current, next) => current.order - next.order);

  readonly stateGroups: FileStateGroup[] = PHASES.map((phase) => ({
    ...phase,
    states: this.stateRows.filter((state) => state.phase === phase.phase)
  })).filter((phase) => phase.states.length > 0);

  constructor(
    private readonly dialogRef: MatDialogRef<FileStatesInfoComponent>
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  getVisibilityText(state: FileStateInfo): string {
    if (state.visibleInOperations && state.visibleInSales) return 'Operations y Sales';
    if (state.visibleInOperations) return 'Operations';
    if (state.visibleInSales) return 'Sales';
    return 'Sistema';
  }

  hasAnyAction(state: FileStateInfo): boolean {
    return (
      state.operationsNextStates.length > 0 ||
      state.salesNextStates.length > 0 ||
      state.automaticNextStates.length > 0
    );
  }
}
